import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, makeStyles, Theme, createStyles } from "@material-ui/core";
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import MapIcon from '@material-ui/icons/Map';
import ImageIcon from '@material-ui/icons/Image';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import UndoIcon from '@material-ui/icons/Undo';
import LoopIcon from '@material-ui/icons/Loop';
import DescriptionIcon from '@material-ui/icons/Description';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ArchiveIcon from '@material-ui/icons/Archive';
import clsx from "clsx";
import { authProvider } from "../../services/Security";
import { isProd } from "../shared/Functions";

export const Navigation = ({ district, operator }) => {
    const history = useHistory();
    let location = useLocation();
    const drawerWidth = 300;
    const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        appBarShift: {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            "& .MuiPaper-root": {
                backgroundColor: "#2f4858",
                color: "#fff",
                "-webkit-box-shadow": "0px 0px 100px 8px rgba(0,0,0,0.27)",
                boxShadow: "0px 0px 100px 8px rgba(0,0,0,0.27)"
            }
        },
        drawerOpen: {
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerClose: {
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: theme.spacing(7) + 1,
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9) + 1,
            },
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: theme.spacing(0, 1),
            // necessary for content to be below app bar
            ...theme.mixins.toolbar,
        },
        content: {
          flexGrow: 1,
          padding: theme.spacing(3),
        }
    }),
    );
    const classes = useStyles();
    const [open, setOpen] = useState<boolean>(false);
    const [canReadBillingAccounts, setCanReadBillingAccounts] = useState<boolean>(false);
    const [canReadServiceAddresses, setCanReadServiceAddresses] = useState<boolean>(false);
    const [canReadAdmins, setCanReadAdmins] = useState<boolean>(false);
    const [canReadMeterReads, setCanReadMeterReads] = useState<boolean>(false);
    const [canReadMeterRoutes, setCanReadMeterRoutes] = useState<boolean>(false);
    const [canReadMeterImages, setCanReadMeterImages] = useState<boolean>(false);

    useEffect(() => {
        authProvider.getNexusToken().then(token => {
            let perms = authProvider.getPermissions(token);
            setCanReadBillingAccounts(perms.some((perm: string) => perm === "ReadBillingAccounts"));
            setCanReadServiceAddresses(perms.some((perm: string) => perm === "ReadServiceAddresses"));
            setCanReadAdmins(perms.some((perm: string) => perm === "ReadAdmin"));
            setCanReadMeterReads(perms.some((perm: string) => perm === "ReadMeterReads"));
            setCanReadMeterRoutes(perms.some((perm: string) => perm === "ReadRoutes"));
            setCanReadMeterImages(perms.some((perm: string) => perm === "ReadMeterReaderImages"));
        })
    }, [])

    return (
        <Drawer
			variant="permanent"
			className={clsx(classes.drawer, {
				[classes.drawerOpen]: open,
				[classes.drawerClose]: !open,
			})}
			classes={{
				paper: clsx({
					[classes.drawerOpen]: open,
					[classes.drawerClose]: !open,
				}),
			}}
        >
			<div className={classes.toolbar}>
				<IconButton onClick={() => setOpen(!open)}>
					{open ? <ChevronLeftIcon style={{ color: "#fff" }} /> : <ChevronRightIcon style={{ color: "#fff" }} />}
				</IconButton>
			</div>
			<Divider />
			<List>
				<ListItem button onClick={() => { setOpen(false); history.replace('/app/payment-report') }}>
					<ListItemIcon><DescriptionIcon style={{ color: location.pathname.startsWith("/app/payment-report") ? "#f28f29" : "#fff" }} /></ListItemIcon>
					<ListItemText primary={"Payment Listing"} />
				</ListItem>
				<ListItem button onClick={() => { setOpen(false); history.replace('/app/autopay-signups') }}>
					<ListItemIcon><LoopIcon style={{ color: location.pathname.startsWith("/app/autopay-signups") ? "#f28f29" : "#fff" }} /></ListItemIcon>
					<ListItemText primary={"Autopay Signups Listing"} />
				</ListItem>
				<ListItem button onClick={() => { setOpen(false); history.replace('/app/bank-reconciliation') }}>
					<ListItemIcon><AssignmentTurnedInIcon style={{ color: location.pathname.startsWith("/app/bank-reconciliation") ? "#f28f29" : "#fff" }} /></ListItemIcon>
					<ListItemText primary={"Bank Reconciliation Report"} />
				</ListItem>
				<ListItem button onClick={() => { setOpen(false); history.replace('/app/bill-archive') }}>
					<ListItemIcon><ArchiveIcon style={{ color: location.pathname.startsWith("/app/bill-archive") ? "#f28f29" : "#fff" }} /></ListItemIcon>
					<ListItemText primary={"Bill Archive"} />
				</ListItem>
				{!isProd() &&
					<>
						<Divider />
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/billing-accounts') }} disabled={!canReadBillingAccounts}>
							<ListItemIcon><AccountBalanceIcon style={{ color: location.pathname.startsWith("/app/billing-accounts") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Billing Accounts"} />
						</ListItem>
						<Divider />
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/autopay') }} disabled={!district}>
							<ListItemIcon><AttachMoneyIcon style={{ color: location.pathname.startsWith("/app/autopay") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Recurring Payments"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/service-addresses') }} disabled={!district || !canReadServiceAddresses}>
							<ListItemIcon><HomeWorkIcon style={{ color: location.pathname.startsWith("/app/service-addresses") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Service Addresses"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/meter-reads') }} disabled={!district || !canReadMeterReads}>
							<ListItemIcon><AccessTimeIcon style={{ color: location.pathname.startsWith("/app/meter-reads") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Meter Reads"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/meter-routes') }} disabled={!district || !canReadMeterRoutes}>
							<ListItemIcon><MapIcon style={{ color: location.pathname.startsWith("/app/meter-routes") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Meter Routes"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/meter-images') }} disabled={!district || !canReadMeterImages}>
							<ListItemIcon><ImageIcon style={{ color: location.pathname.startsWith("/app/meter-images") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Meter Images"} />
						</ListItem>
						<Divider />
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/admins') }} disabled={!operator || !canReadAdmins}>
							<ListItemIcon><SupervisorAccountIcon style={{ color: location.pathname.startsWith("/app/admins") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Admins"} />
						</ListItem>
						<Divider />
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/payment-flow') }}>
							<ListItemIcon><AttachMoneyIcon style={{ color: location.pathname.startsWith("/app/payment-flow") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Payment Flow"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/wallet') }}>
							<ListItemIcon><CreditCardIcon style={{ color: location.pathname.startsWith("/app/wallet") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Wallet"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/recurring') }}>
							<ListItemIcon><AttachMoneyIcon style={{ color: location.pathname.startsWith("/app/recurring") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Recurring Payments"} />
						</ListItem>
						<ListItem button onClick={() => { setOpen(false); history.replace('/app/returns') }}>
							<ListItemIcon><UndoIcon style={{ color: location.pathname.startsWith("/app/returns") ? "#f28f29" : "#fff" }} /></ListItemIcon>
							<ListItemText primary={"Chargebacks/Returns"} />
						</ListItem>
					</>
				}
			</List>
    	</Drawer>
    );
};